import React from "react";
import type { FunctionComponent } from "react";
import { useBackfillJob } from "../../hooks/useBackfillJob";
import { isSyncJob } from "./gameSync";
import { useRefreshGamesOnJobEnd } from "./useRefreshGamesOnJobEnd";

/**
 * App-wide progress strip for the Pro signup backfill. A sync of the player's new games (#259) shows nothing here.
 *
 * Mounted in the shell rather than on one page because the job runs 40-90 minutes and the user
 * navigates freely during it — a Profile-only card would vanish the moment they opened Library.
 * Renders nothing when there's no job, so it costs a non-Pro user nothing. Also the one place that refreshes the
 * games lists when a job lands its games (it's always mounted, so it sees every job end).
 */
export const BackfillBanner: FunctionComponent = () => {
  const progress = useBackfillJob();
  useRefreshGamesOnJobEnd(progress);
  const { job, isRunning, isFailed, percent } = progress;

  // A sync shows nothing here: its new games are rows in Recent games and the Library, marked "Analyzing…" while it
  // runs (Sam, 2026-10-16: "I don't want it to say analyzing two new games at the top, i want them to show up under
  // Recent Games"). No outcome, no failure strip (the next sync retries). First, so a sync's outcome can never reach
  // the backfill's "No games found" below.
  if (isSyncJob(job)) return null;

  // A job that completed WITHOUT analyzing anything needs its own message. A Pro user who subscribed and
  // saw a progress bar flash to nothing has no way to tell success from failure — and "already_complete"
  // is the common case whenever another account already linked the same chess username (analysis is
  // shared by gameOriginId, so their library is instantly full). Shown before the isRunning/isFailed
  // gate because such a job is neither.
  if (job?.outcome && !isRunning) {
    const ready = job.outcome === 'already_complete';
    return (
      <div role="status" className="flex items-center gap-2.5 px-4 py-2 border-b border-[var(--glass-border)] bg-[var(--glass-bg)]">
        <span className="text-[12px] font-semibold text-on-surface">
          {ready ? 'Your games are ready' : 'No games found'}
        </span>
        <span className="text-[12px] text-subdued">
          {ready
            ? `All ${job.gamesTotal.toLocaleString()} of your analyzed games are already in your library.`
            : 'We couldn’t find any games for that username — check it on your Profile.'}
        </span>
      </div>
    );
  }

  // Success needs no banner: the games simply appear (the hook invalidates the caches).
  if (!job || (!isRunning && !isFailed)) return null;

  if (isFailed) {
    return (
      <div role="status" className="flex items-center gap-2.5 px-4 py-2 border-b border-[rgba(239,68,68,0.25)] bg-[rgba(239,68,68,0.08)]">
        <span className="text-[12px] font-semibold text-[var(--accuracy-low)]">
          Import didn’t finish
        </span>
        <span className="text-[12px] text-subdued">
          We analyzed {job.gamesComplete.toLocaleString()}
          {job.gamesTotal > 0 ? ` of ${job.gamesTotal.toLocaleString()}` : ""} games
          {" before stopping"}. Contact support and we’ll re-run it.
        </span>
      </div>
    );
  }

  // Before the fetch completes, gamesTotal is 0 — say what's happening rather than show "0 of 0".
  const counting = percent == null;
  return (
    <div role="status" aria-live="polite" className="flex items-center gap-3 px-4 py-2 border-b border-[var(--glass-border)] bg-[var(--glass-bg)]">
      <span className="relative flex w-[7px] h-[7px] shrink-0" aria-hidden="true">
        <span className="absolute inline-flex w-full h-full rounded-full bg-[var(--color-accent)] opacity-60 motion-safe:animate-ping" />
        <span className="relative inline-flex w-[7px] h-[7px] rounded-full bg-[var(--color-accent)]" />
      </span>
      <span className="text-[12px] font-semibold text-on-surface shrink-0">Analyzing your games</span>
      {counting ? (
        <span className="text-[12px] text-subdued">Fetching your history…</span>
      ) : (
        <>
          <span className="flex-1 max-w-[220px] h-[4px] rounded-full bg-[rgba(255,255,255,0.08)] overflow-hidden">
            <span className="block h-[4px] rounded-full bg-[var(--color-accent)] transition-[width] duration-500" style={{ width: `${percent}%` }} />
          </span>
          <span className="font-mono text-[11.5px] text-subdued tabular-nums shrink-0">
            {job.gamesComplete.toLocaleString()} / {job.gamesTotal.toLocaleString()}
          </span>
        </>
      )}
      <span className="text-[11.5px] text-faint hidden sm:inline">
        You can keep using the app — new games appear as they finish.
      </span>
    </div>
  );
};
